import { Button, Col, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { ApiDashboard } from '../api';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import FormField from '../form/FormField';
import { FormikState } from 'formik/dist/types';
import { useDeleteDashboards, useSaveDashboards } from './dashboardActions';
import { useDispatch } from 'react-redux';
import { CAN_MODIFY_DASHBOARD } from '../auth/permissions';
import Has from '../auth/Has';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../common/errors/helpers';
import { SalvModal } from '@salvapp/ui-components';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  dashboardId: Yup.number().required('Required'),
});

export interface Props {
  dashboard?: ApiDashboard;
}

export const DashboardModal = ({ dashboard }: Props) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { mutateAsync: saveDashboard } = useSaveDashboards();
  const { mutateAsync: deleteDashboard } = useDeleteDashboards();

  const initialValues =
    dashboard !== undefined
      ? {
          ...dashboard,
        }
      : {
          dashboardId: 0,
          name: '',
          hidden: false,
        };

  const initialFormikState: Partial<FormikState<ApiDashboard>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const handleChange = async (dashboard: ApiDashboard) => {
    await saveDashboard(dashboard);
  };

  const submit = async (values: ApiDashboard, { setStatus, setSubmitting, resetForm }: FormikHelpers<ApiDashboard>) => {
    try {
      await handleChange(values);
      setShowModal(false);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    resetForm(initialFormikState);
    setSubmitting(false);
  };

  const onDeleteDashboard = async (dashboard: ApiDashboard) => {
    if (dashboard.id) {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          confirmationText: 'Yes, delete  the dashboard!',
          cancelText: 'No, keep the dashboard',
          onConfirm: async () => {
            await deleteDashboard(dashboard.id!);
          },
        })
      );
    }
  };

  return (
    <Formik onSubmit={submit} validationSchema={validationSchema} initialValues={initialValues}>
      {({ isSubmitting }: FormikProps<ApiDashboard>) => {
        return (
          <React.Fragment>
            <Has allPermissions={CAN_MODIFY_DASHBOARD}>
              {(dashboard && (
                <Button onClick={() => setShowModal(true)} className='right-button' variant='outline-danger' size='sm'>
                  Edit
                </Button>
              )) || (
                <Button variant='info' size='sm' className='btn-white' onClick={() => setShowModal(true)}>
                  Add new dashboard
                </Button>
              )}
            </Has>
            <SalvModal show={showModal} onHide={() => setShowModal(false)}>
              <Form>
                <SalvModal.Header>{dashboard ? 'Edit dashboard' : 'Add new dashboard'} </SalvModal.Header>

                <SalvModal.Body>
                  <Row>
                    <Col md={12}>
                      <FormField name='name' type='text' label='Name' placeholder='Enter name' />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <FormField
                        name='dashboardId'
                        type='number'
                        label='Dashboard Id'
                        placeholder='Enter dashboard Id'
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className='d-flex justify-content-end'>
                      <FormField id='hidden' name='hidden' type='checkbox' label='Hidden' />
                    </Col>
                  </Row>
                </SalvModal.Body>

                <SalvModal.Footer>
                  <Button variant='secondary' onClick={() => setShowModal(false)}>
                    Close
                  </Button>
                  {dashboard && (
                    <Button disabled={isSubmitting} onClick={() => onDeleteDashboard(dashboard)} variant='danger'>
                      Delete
                    </Button>
                  )}
                  <Button disabled={isSubmitting} type='submit' variant='primary'>
                    Save
                  </Button>
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

export default DashboardModal;
